import { useCallback, useContext } from "react";

import { useHistory } from "react-router-dom";

import { QueryParamsContext } from "../../../../context/QueryParamsContext";
import { notify } from "../../../../utils/notify";
import { MotivoCancelamentoContext } from "../context/MotivoCancelamentoContext";
import { formatDateGetTime } from "../../../../utils/formatDate";
import {
  store as motivoCancelamentoStore,
  update as motivoCancelamentoUpdate,
  destroy as destroyMotivoCancelamento,
} from "../../../../repositories/qerpapi/erp/faturamento/MotivoCancelamentoRepository";
import { validNotNull } from "../../../../utils/validations";
import { findEmpresasV2 } from "../../../../repositories/qerpapi/erp/prouser/v2/EmpresasRepository";
import { show as showMotivoCancelamento } from "../../../../repositories/qerpapi/erp/faturamento/MotivoCancelamentoRepository";

const useMotivoCancelamentoController = () => {
  const {
    setLoading,
    description,
    released,
    setErrorsSubmit,
    setDescription,
    setReleased,
    companies,
    setCompanies,
    setDataTableReload,
    id,
    setId,
  } = useContext(MotivoCancelamentoContext);

  const { queryParams } = useContext(QueryParamsContext);

  const { push } = useHistory();

  const handleCheckCompany = (companyId) => {
    const copyCheckCompanies = [...companies];
    const companyIndex = copyCheckCompanies.findIndex(
      (comp) => comp.id === companyId
    );

    copyCheckCompanies[companyIndex] = {
      ...copyCheckCompanies[companyIndex],
      check: !copyCheckCompanies[companyIndex].check,
    };

    if (!copyCheckCompanies.filter((e) => e.check).length)
      return notify("Ao menos uma empresa deve estar selecionada.");

    setCompanies(copyCheckCompanies);
  };

  const rulesToStore = (validate) => {
    if (!description) {
      validate.errorsValidation.description = true;
      validate.error = true;
    }

    return validate;
  };

  const storeAndUpdate = async (refresh = false) => {
    const jsonValidMain = [
      {
        key: "description",
        value: description,
      },
    ];

    const validateSubmit = rulesToStore(validNotNull(jsonValidMain));

    setErrorsSubmit({
      ...validateSubmit.errorsValidation,
    });

    if (validateSubmit.error)
      return notify(
        "Verifique se os campos foram preenchidos corretamente.",
        true,
        "warning"
      );

    try {
      setLoading(true);

      var callback;
      const now = new Date();

      const body = {
        motivocancelamento: {
          descricao: description,
          liberado: released ? "S" : "N",
          tipo_status: "C",
          data: formatDateGetTime({
            date: now,
            separator: "-",
            format: "yyyy-mm-dd-hh:mm:ss",
          }),
          motivocancelamentoempresa: [],
        },
      };

      companies.forEach((company) => {
        if (company.check)
          body?.motivocancelamento?.motivocancelamentoempresa.push({
            idempresa: company.id,
          });
      });

      if (id) {
        callback = await motivoCancelamentoUpdate({
          companyCnpj: queryParams?.empresa_cnpj,
          id: id,
          body,
        });
      } else {
        callback = await motivoCancelamentoStore({
          companyCnpj: queryParams?.empresa_cnpj,
          body,
        });
      }

      if (!refresh) push("listagem?gfAccessToken=" + queryParams.gfAccessToken);
      clearContext();

      if (!callback.success)
        return notify("Erro ao salvar motivo de cancelamento.");
      else {
        return notify(
          `Motivo de Cancelamento ${id ? "Atualizado" : "Cadastrado"}.`,
          true,
          "success"
        );
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const clearContext = useCallback(() => {
    setDescription("");
    setReleased(true);
    setCompanies((current) => current.map((e) => ({ ...e, check: true })));
    setErrorsSubmit({});
    setId(null);
  }, [setCompanies, setDescription, setErrorsSubmit, setReleased, setId]);

  const destroy = async (id) => {
    try {
      setLoading(true);
      const callback = await destroyMotivoCancelamento({
        companyCnpj: queryParams?.empresa_cnpj,
        id: id,
      });

      if (!callback?.success)
        return notify("Erro ao excluir motivo de cancelamento");
      else
        return notify(
          "Motivo de cancelamento excluido com sucesso.",
          true,
          "success"
        );
    } catch (error) {
      console.error(error);
    } finally {
      setDataTableReload(true);
      setLoading(false);
    }
  };

  const handleSetData = useCallback(
    async (id) => {
      if (!queryParams?.empresa_cnpj) return;

      clearContext();

      try {
        setLoading(true);

        const [motivoCancelamento, companies] = await Promise.all([
          showMotivoCancelamento({
            companyCnpj: queryParams?.empresa_cnpj,
            id: id ?? null,
          }),
          findEmpresasV2(queryParams?.empresa_cnpj),
        ]);

        if (!companies?.success || !motivoCancelamento?.success)
          return notify("Erro ao buscar dados.");

        const checks = companies.data.map((companyExist) => {
          if (id) {
            return {
              ...companyExist,
              check: (
                motivoCancelamento?.data?.motivocancelamentoempresa || []
              ).some((comp) => comp?.idempresa === companyExist?.id),
            };
          } else
            return {
              ...companyExist,
              check: true,
            };
        });

        setDescription(motivoCancelamento?.data?.descricao || "");
        setReleased(
          motivoCancelamento?.data?.liberado
            ? motivoCancelamento?.data?.liberado === "S"
            : true
        );
        setCompanies(checks);
        setId(id ?? null);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    },
    [
      queryParams?.empresa_cnpj,
      setLoading,
      setDescription,
      setReleased,
      setCompanies,
      clearContext,
      setId,
    ]
  );

  const back = () => {
    clearContext();
    push("listagem?gfAccessToken=" + queryParams.gfAccessToken);
  };

  return {
    handleCheckCompany,
    storeAndUpdate,
    clearContext,
    destroy,
    back,
    handleSetData,
  };
};

export default useMotivoCancelamentoController;
